import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { TOAST_CONFIG } from '../../shared/Constants';
import {
  connectNotificationChannel,
  disconnectNotificationChannel,
  NotificationChannel,
  TaskApiError,
  verifyNotificationChannel,
  WebhookChannelName,
} from '../../shared/services/scheduledTasksApi';
import { TelegramGlyph, WEBHOOK_PLATFORMS, WebhookPlatformInfo } from './webhookPlatforms';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
`;

const Tile = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
`;

const TileIcon = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: ${({ theme }) => theme.hover_overlay_light};
  color: ${({ theme }) => theme.font_color};
  & svg { font-size: 1.15rem; }
`;

const TileName = styled.div`
  font-weight: 600;
  font-size: 0.92rem;
  color: ${({ theme }) => theme.font_color};
`;

const TileHint = styled.div`
  font-size: 0.8rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.text_secondary};
  margin-top: 2px;
`;

const PlatformBox = styled.div`
  grid-column: 1 / -1;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  padding: 14px 16px;
`;

const PlatformHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Spacer = styled.div`
  flex: 1;
  min-width: 0;
`;

const GhostBtn = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 7px 12px;
  border-radius: 9px;
  font-size: 0.84rem;
  font-weight: 550;
  cursor: pointer;
  color: ${({ theme }) => theme.font_color};
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; }
  &:disabled { opacity: 0.4; cursor: default; }
  & svg { font-size: 1rem; }
`;

const Binding = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 0;
  border-top: 1px solid ${({ theme }) => theme.sidebar_border};
  &:first-of-type { margin-top: 12px; }
`;

const Alias = styled.span`
  font-weight: 600;
  font-size: 0.88rem;
  color: ${({ theme }) => theme.font_color};
`;

const Masked = styled.span`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.text_secondary};
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 260px;
`;

const Pill = styled.span<{ $ok?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.74rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  color: ${({ $ok, theme }) => ($ok ? theme.plan_success_color || '#2e7d32' : theme.plan_warning_color || '#b26a00')};
  background: ${({ theme }) => theme.hover_overlay_light};
  & svg { font-size: 0.85rem; }
`;

const IconBtn = styled.button`
  all: unset;
  display: flex;
  padding: 6px;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.text_secondary};
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; color: ${({ theme }) => theme.error_message_text || '#d32f2f'}; }
  & svg { font-size: 1.1rem; }
`;

const Field = styled.input`
  box-sizing: border-box;
  padding: 8px 11px;
  border-radius: 9px;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  font-size: 0.86rem;
  outline: none;
  min-width: 0;
  &:focus { border-color: ${({ theme }) => theme.font_color}; }
  &::placeholder { color: ${({ theme }) => theme.text_secondary}; }
`;

const AddForm = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.sidebar_border};
  & > input:first-child { flex: 0 1 150px; }
  & > input:nth-child(2) { flex: 1 1 260px; }
`;

const FormHint = styled.div`
  flex-basis: 100%;
  font-size: 0.78rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.text_secondary};
  & a { color: inherit; }
`;

const errorMessage = (e: unknown, fallback: string) => (e instanceof TaskApiError ? e.message : fallback);

interface PlatformCardProps {
  platform: WebhookPlatformInfo;
  bindings: NotificationChannel[];
  onChanged: () => void;
}

/** Webhooks de uma plataforma: lista com estado, verificação por código e
 * formulário para conectar um novo (apelido + URL). */
const PlatformCard: React.FC<PlatformCardProps> = ({ platform, bindings, onChanged }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [adding, setAdding] = useState(false);
  const [alias, setAlias] = useState('');
  const [url, setUrl] = useState('');
  const [codes, setCodes] = useState<Record<string, string>>({});
  const [confirmingRemove, setConfirmingRemove] = useState<string | null>(null);
  // Operação em andamento ('add' | 'verify:<alias>' | 'remove:<alias>').
  const [busy, setBusy] = useState<string | null>(null);
  const channel: WebhookChannelName = platform.name;
  const { Glyph } = platform;
  const verified = bindings.filter((c) => c.verified).length;

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    const a = alias.trim().toLowerCase();
    const u = url.trim();
    if (!a || !u || busy) return;
    setBusy('add');
    try {
      const token = await getAccessTokenSilently();
      await connectNotificationChannel(token, channel, u, a);
      setAlias('');
      setUrl('');
      setAdding(false);
      toast.success(`Enviamos um código para o canal do ${platform.label}. Digite-o abaixo para confirmar.`, {
        ...TOAST_CONFIG,
        autoClose: 4000,
      });
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err, `Não foi possível conectar o ${platform.label}.`), TOAST_CONFIG);
    } finally {
      setBusy(null);
    }
  };

  const handleVerify = async (a: string) => {
    const code = (codes[a] || '').trim();
    if (!code || busy) return;
    setBusy(`verify:${a}`);
    try {
      const token = await getAccessTokenSilently();
      await verifyNotificationChannel(token, channel, code, a);
      setCodes((prev) => ({ ...prev, [a]: '' }));
      toast.success(`${platform.label} conectado!`, { ...TOAST_CONFIG, autoClose: 2500 });
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err, 'Código inválido. Tente novamente.'), TOAST_CONFIG);
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async (a: string) => {
    if (busy) return;
    setBusy(`remove:${a}`);
    try {
      const token = await getAccessTokenSilently();
      await disconnectNotificationChannel(token, channel, a);
      setConfirmingRemove(null);
      toast.success('Webhook removido', { ...TOAST_CONFIG, autoClose: 2000 });
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err, 'Não foi possível remover o webhook.'), TOAST_CONFIG);
    } finally {
      setBusy(null);
    }
  };

  return (
    <PlatformBox>
      <PlatformHeader>
        <TileIcon><Glyph /></TileIcon>
        <Spacer>
          <TileName>{platform.label}</TileName>
          <TileHint>
            {verified > 0
              ? `${verified} canal${verified > 1 ? 'is' : ''} conectado${verified > 1 ? 's' : ''}. Peça "manda no ${platform.label} (apelido)".`
              : `Conecte um webhook para receber os resultados num canal do ${platform.label}.`}
          </TileHint>
        </Spacer>
        {!adding && (
          <GhostBtn onClick={() => setAdding(true)}>
            <AddIcon /> Conectar
          </GhostBtn>
        )}
      </PlatformHeader>

      {bindings.map((ch) => (
        <Binding key={ch.alias}>
          <Alias>{ch.alias}</Alias>
          <Masked title={ch.address_masked}>{ch.address_masked}</Masked>
          {ch.verified ? (
            <Pill $ok><CheckCircleIcon /> Conectado</Pill>
          ) : (
            <Pill>Aguardando código</Pill>
          )}
          <Spacer />
          {!ch.verified && (
            <>
              <Field
                value={codes[ch.alias] || ''}
                onChange={(e) => setCodes((prev) => ({ ...prev, [ch.alias]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') handleVerify(ch.alias); }}
                placeholder="Código"
                aria-label={`Código de verificação de ${ch.alias}`}
                style={{ width: 110 }}
              />
              <GhostBtn onClick={() => handleVerify(ch.alias)} disabled={!!busy || !(codes[ch.alias] || '').trim()}>
                {busy === `verify:${ch.alias}` ? 'Verificando…' : 'Verificar'}
              </GhostBtn>
            </>
          )}
          {confirmingRemove === ch.alias ? (
            <>
              <GhostBtn onClick={() => handleRemove(ch.alias)} disabled={!!busy}>
                {busy === `remove:${ch.alias}` ? 'Removendo…' : 'Remover'}
              </GhostBtn>
              <GhostBtn onClick={() => setConfirmingRemove(null)} disabled={!!busy}>Cancelar</GhostBtn>
            </>
          ) : (
            <IconBtn onClick={() => setConfirmingRemove(ch.alias)} aria-label={`Remover ${ch.alias}`} title="Remover">
              <DeleteOutlineIcon />
            </IconBtn>
          )}
        </Binding>
      ))}

      {adding && (
        <AddForm onSubmit={handleConnect}>
          <Field
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            placeholder="Apelido (ex.: time-eng)"
            aria-label="Apelido do canal"
            autoFocus
          />
          <Field
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={platform.placeholder}
            aria-label="URL do webhook"
          />
          <GhostBtn type="submit" disabled={!!busy || !alias.trim() || !url.trim()}>
            {busy === 'add' ? 'Enviando…' : 'Enviar código'}
          </GhostBtn>
          <GhostBtn type="button" onClick={() => setAdding(false)}>Cancelar</GhostBtn>
          <FormHint>
            {platform.helpText}{' '}
            <a href={platform.helpUrl} target="_blank" rel="noopener noreferrer">Como criar</a>.
            {' '}Mandamos um código para o canal para confirmar que ele é seu.
          </FormHint>
        </AddForm>
      )}
    </PlatformBox>
  );
};

interface TelegramCardProps {
  bindings: NotificationChannel[];
  onChanged: () => void;
}

// Quanto tempo a página espera o usuário tocar em Iniciar (= validade do link).
const LINK_TTL_MS = 15 * 60 * 1000;

/** Telegram: sem URL para colar. "Conectar" gera um link t.me/<bot>?start=…;
 * o usuário toca em Iniciar no bot e a página percebe (polling) quando o
 * destino fica verificado. */
const TelegramCard: React.FC<TelegramCardProps> = ({ bindings, onChanged }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [adding, setAdding] = useState(false);
  const [alias, setAlias] = useState('pessoal');
  const [pending, setPending] = useState<{ alias: string; link: string; since: number } | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const verified = bindings.filter((c) => c.verified).length;

  // Enquanto há um link aberto, atualiza a lista até o destino ficar verificado.
  useEffect(() => {
    if (!pending) return;
    const id = window.setInterval(() => {
      if (Date.now() - pending.since > LINK_TTL_MS) {
        setPending(null);
        return;
      }
      onChanged();
    }, 3000);
    return () => window.clearInterval(id);
  }, [pending, onChanged]);

  useEffect(() => {
    if (!pending) return;
    const b = bindings.find((c) => c.alias === pending.alias);
    if (b?.verified) {
      setPending(null);
      toast.success(`Telegram conectado (${b.address_masked}).`, { ...TOAST_CONFIG, autoClose: 3000 });
    }
  }, [bindings, pending]);

  const requestLink = async (a: string) => {
    if (busy) return;
    setBusy(`link:${a}`);
    try {
      const token = await getAccessTokenSilently();
      const res = await connectNotificationChannel(token, 'telegram', '', a);
      if (res.link) {
        setPending({ alias: res.alias, link: res.link, since: Date.now() });
        window.open(res.link, '_blank', 'noopener,noreferrer');
      }
      setAdding(false);
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err, 'Não foi possível gerar o link do Telegram.'), TOAST_CONFIG);
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async (a: string) => {
    if (busy) return;
    setBusy(`remove:${a}`);
    try {
      const token = await getAccessTokenSilently();
      await disconnectNotificationChannel(token, 'telegram', a);
      setConfirmingRemove(null);
      if (pending?.alias === a) setPending(null);
      toast.success('Telegram desconectado', { ...TOAST_CONFIG, autoClose: 2000 });
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err, 'Não foi possível desconectar o Telegram.'), TOAST_CONFIG);
    } finally {
      setBusy(null);
    }
  };

  return (
    <PlatformBox>
      <PlatformHeader>
        <TileIcon><TelegramGlyph /></TileIcon>
        <Spacer>
          <TileName>Telegram</TileName>
          <TileHint>
            {verified > 0
              ? 'Conectado. Peça "manda no Telegram" ao criar a tarefa.'
              : 'Receba os resultados numa conversa com o bot da Maritaca.'}
          </TileHint>
        </Spacer>
        {!adding && !pending && (
          <GhostBtn onClick={() => setAdding(true)}>
            <AddIcon /> Conectar
          </GhostBtn>
        )}
      </PlatformHeader>

      {bindings.map((ch) => (
        <Binding key={ch.alias}>
          <Alias>{ch.alias}</Alias>
          <Masked title={ch.address_masked}>{ch.address_masked}</Masked>
          {ch.verified ? (
            <Pill $ok><CheckCircleIcon /> Conectado</Pill>
          ) : (
            <Pill>Aguardando Iniciar</Pill>
          )}
          <Spacer />
          {!ch.verified && pending?.alias !== ch.alias && (
            <GhostBtn onClick={() => requestLink(ch.alias)} disabled={!!busy}>
              Gerar novo link
            </GhostBtn>
          )}
          {confirmingRemove === ch.alias ? (
            <>
              <GhostBtn onClick={() => handleRemove(ch.alias)} disabled={!!busy}>
                {busy === `remove:${ch.alias}` ? 'Removendo…' : 'Remover'}
              </GhostBtn>
              <GhostBtn onClick={() => setConfirmingRemove(null)} disabled={!!busy}>Cancelar</GhostBtn>
            </>
          ) : (
            <IconBtn onClick={() => setConfirmingRemove(ch.alias)} aria-label={`Remover ${ch.alias}`} title="Remover">
              <DeleteOutlineIcon />
            </IconBtn>
          )}
        </Binding>
      ))}

      {pending && (
        <AddForm as="div">
          <GhostBtn as="a" href={pending.link} target="_blank" rel="noopener noreferrer">
            <TelegramGlyph size={16} /> Abrir o Telegram
          </GhostBtn>
          <FormHint>
            Toque em <strong>Iniciar</strong> na conversa com o bot. Esta página atualiza sozinha quando
            o Telegram confirmar (o link vale por 15 minutos).
          </FormHint>
        </AddForm>
      )}

      {adding && (
        <AddForm onSubmit={(e) => { e.preventDefault(); requestLink(alias.trim().toLowerCase()); }}>
          <Field
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            placeholder="Apelido (ex.: pessoal)"
            aria-label="Apelido do destino"
            autoFocus
          />
          <GhostBtn type="submit" disabled={!!busy || !alias.trim()}>
            {busy ? 'Gerando…' : 'Gerar link'}
          </GhostBtn>
          <GhostBtn type="button" onClick={() => setAdding(false)}>Cancelar</GhostBtn>
          <FormHint>Abrimos o Telegram numa conversa com o bot da Maritaca; é só tocar em Iniciar.</FormHint>
        </AddForm>
      )}
    </PlatformBox>
  );
};

interface DeliveryChannelsProps {
  channels: NotificationChannel[];
  onChanged: () => void;
  /** Bot do Telegram configurado no servidor (/info/features). */
  telegramEnabled?: boolean;
}

/** Onde os resultados podem chegar: a conversa e o e-mail não precisam de
 * configuração; Telegram vincula pelo bot; Slack, Discord e Teams aceitam
 * vários webhooks cada, com apelido e verificação por código (a tarefa usa
 * "<canal>:<apelido>"). */
const DeliveryChannels: React.FC<DeliveryChannelsProps> = ({ channels, onChanged, telegramEnabled }) => (
  <Grid>
    <Tile>
      <TileIcon><ChatBubbleOutlineIcon /></TileIcon>
      <div>
        <TileName>Conversa</TileName>
        <TileHint>Sempre ativo: o resultado aparece na conversa onde a tarefa foi criada.</TileHint>
      </div>
    </Tile>
    <Tile>
      <TileIcon><MailOutlineIcon /></TileIcon>
      <div>
        <TileName>E-mail</TileName>
        <TileHint>Vai para o e-mail da sua conta. Basta pedir "me manda por e-mail".</TileHint>
      </div>
    </Tile>
    {telegramEnabled && (
      <TelegramCard bindings={channels.filter((c) => c.channel === 'telegram')} onChanged={onChanged} />
    )}
    {WEBHOOK_PLATFORMS.map((p) => (
      <PlatformCard
        key={p.name}
        platform={p}
        bindings={channels.filter((c) => c.channel === p.name)}
        onChanged={onChanged}
      />
    ))}
  </Grid>
);

export default DeliveryChannels;
